

import { Platform } from '@angular/cdk/platform';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';

import { MwcConfigKey, MwcConfigService, WithConfig } from 'mwc/core/config';
import { NgClassInterface, NumberInput, MwcShapeSCType, MwcSizeLDSType } from 'mwc/core/types';
import { InputNumber } from 'mwc/core/util';

const NZ_CONFIG_MODULE_NAME: MwcConfigKey = 'avatar';

@Component({
  selector: 'mwc-avatar',
  exportAs: 'mwcAvatar',
  template: `
    <span mwc-icon *ngIf="mwcIcon && hasIcon" [mwcIconfont]="mwcIcon"></span>
    <img *ngIf="mwcSrc && hasSrc" [src]="mwcSrc" [attr.srcset]="mwcSrcSet" [attr.alt]="mwcAlt" (error)="imgError($event)" />
    <span class="mwc-avatar-string" #textEl *ngIf="mwcText && hasText">{{ mwcText }}</span>
  `,
  host: {
    class: 'mwc-avatar',
    '[class.mwc-avatar-lg]': `mwcSize === 'large'`,
    '[class.mwc-avatar-sm]': `mwcSize === 'small'`,
    '[class.mwc-avatar-square]': `mwcShape === 'square'`,
    '[class.mwc-avatar-circle]': `mwcShape === 'circle'`,
    '[class.mwc-avatar-icon]': `mwcIcon`,
    '[class.mwc-avatar-image]': `hasSrc `,
    '[style.width]': 'customSize',
    '[style.height]': 'customSize',
    '[style.line-height]': 'customSize',
    // mwcSize type is number when customSize is true
    '[style.font-size.px]': '(hasIcon && customSize) ? $any(mwcSize) / 2 : null'
  },
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class MwcAvatarComponent implements OnChanges {
  static ngAcceptInputType_mwcGap: NumberInput;

  readonly _mwcModuleName: MwcConfigKey = NZ_CONFIG_MODULE_NAME;
  @Input() @WithConfig() mwcShape: MwcShapeSCType = 'circle';
  @Input() @WithConfig() mwcSize: MwcSizeLDSType | number = 'default';
  @Input() @WithConfig() @InputNumber() mwcGap = 4;
  @Input() mwcText?: string;
  @Input() mwcSrc?: string;
  @Input() mwcSrcSet?: string;
  @Input() mwcAlt?: string;
  @Input() mwcIcon?: string;
  @Output() readonly mwcError = new EventEmitter<Event>();

  hasText: boolean = false;
  hasSrc: boolean = true;
  hasIcon: boolean = false;
  classMap: NgClassInterface = {};
  customSize: string | null = null;

  @ViewChild('textEl', { static: false }) textEl?: ElementRef<HTMLSpanElement>;

  private el: HTMLElement = this.elementRef.nativeElement;

  constructor(
    public mwcConfigService: MwcConfigService,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private platform: Platform,
    private ngZone: NgZone
  ) {}

  imgError($event: Event): void {
    this.mwcError.emit($event);
    if (!$event.defaultPrevented) {
      this.hasSrc = false;
      this.hasIcon = false;
      this.hasText = false;
      if (this.mwcIcon) {
        this.hasIcon = true;
      } else if (this.mwcText) {
        this.hasText = true;
      }
      this.cdr.detectChanges();
      this.setSizeStyle();
      this.notifyCalc();
    }
  }

  ngOnChanges(): void {
    this.hasText = !this.mwcSrc && !!this.mwcText;
    this.hasIcon = !this.mwcSrc && !!this.mwcIcon;
    this.hasSrc = !!this.mwcSrc;

    this.setSizeStyle();
    this.notifyCalc();
  }

  private calcStringSize(): void {
    if (!this.hasText) {
      return;
    }

    const textEl = this.textEl!.nativeElement;
    const childrenWidth = textEl.offsetWidth;
    const avatarWidth = this.el.getBoundingClientRect().width;
    const offset = this.mwcGap * 2 < avatarWidth ? this.mwcGap * 2 : 8;
    const scale = avatarWidth - offset < childrenWidth ? (avatarWidth - offset) / childrenWidth : 1;

    textEl.style.transform = `scale(${scale}) translateX(-50%)`;
    textEl.style.lineHeight = this.customSize || '';
  }

  private notifyCalc(): void {
    // If use ngAfterViewChecked, always demands more computations, so......
    if (this.platform.isBrowser) {
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.calcStringSize();
        });
      });
    }
  }

  private setSizeStyle(): void {
    if (typeof this.mwcSize === 'number') {
      this.customSize = `${this.mwcSize}px`;
    } else {
      this.customSize = null;
    }
    this.cdr.markForCheck();
  }
}
